/**
 * Absage einer Event-Teilnahme über den Link aus der Bestätigungs- bzw.
 * Erinnerungsmail. Der Token ist zufällig und nur für genau diese Anmeldung
 * gültig; der Nutzer bestätigt die Absage mit einem Klick.
 */
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/Layout";
import { cancelEventParticipation, lookupEventCancellation } from "@/lib/events.functions";
import { formatEventDate, type CancelLookup } from "@/lib/events";

export const Route = createFileRoute("/events/absagen")({
  validateSearch: (search: Record<string, unknown>) => ({
    t: typeof search.t === "string" ? search.t : "",
  }),
  loaderDeps: ({ search }) => ({ t: search.t }),
  loader: async ({ deps }): Promise<{ lookup: CancelLookup }> => {
    if (!/^[a-f0-9]{48}$/.test(deps.t)) return { lookup: { status: "invalid" } };
    return { lookup: await lookupEventCancellation({ data: { token: deps.t } }) };
  },
  head: () => ({
    meta: [
      { title: "Teilnahme absagen | creategy" },
      { name: "description", content: "Absage einer Veranstaltungsteilnahme bei creategy." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Teilnahme absagen | creategy" },
      { property: "og:description", content: "Absage einer Veranstaltungsteilnahme bei creategy." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: CancelPage,
});

function CancelPage() {
  const { lookup } = Route.useLoaderData();
  const { t } = Route.useSearch();
  const cancelFn = useServerFn(cancelEventParticipation);
  const [state, setState] = useState<CancelLookup>(lookup);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      setState(await cancelFn({ data: { token: t } }));
    } catch {
      setError("Die Absage konnte nicht übernommen werden. Bitte versuchen Sie es erneut.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Layout>
      <section className="section-y mx-auto w-full max-w-[720px] px-6 md:px-12 font-body text-foreground">
        {state.status === "invalid" && (
          <>
            <h1 className="font-display text-3xl font-extrabold text-primary">Link ungültig</h1>
            <p className="mt-4">
              Dieser Absage-Link ist nicht gültig. Bitte nutzen Sie den Link aus Ihrer E-Mail oder{" "}
              <Link to="/kontakt" className="underline underline-offset-4">
                schreiben Sie uns
              </Link>
              .
            </p>
          </>
        )}
        {state.status === "already" && (
          <>
            <h1 className="font-display text-3xl font-extrabold text-primary">Teilnahme abgesagt</h1>
            <p className="mt-4">
              Ihre Teilnahme an „{state.title}“ ist abgesagt. Schade – vielleicht klappt es beim nächsten Mal.
            </p>
          </>
        )}
        {state.status === "open" && (
          <>
            <h1 className="font-display text-3xl font-extrabold text-primary">Teilnahme absagen?</h1>
            <p className="mt-4">
              Sie sind {state.plusOne ? "mit Begleitung" : ""} für „{state.title}“ am{" "}
              {formatEventDate(state.startsAt)} angemeldet. Möchten Sie Ihre Teilnahme absagen?
            </p>
            {error && (
              <p className="mt-4 text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={confirm}
                disabled={busy}
                className="inline-flex items-center justify-center rounded-md bg-brand-button px-6 py-3 font-display text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {busy ? "Wird abgesagt…" : "Ja, Teilnahme absagen"}
              </button>
              <Link
                to="/events"
                className="inline-flex items-center justify-center rounded-md border border-border px-6 py-3 font-display text-sm font-extrabold text-foreground"
              >
                Nein, ich komme
              </Link>
            </div>
          </>
        )}
      </section>
    </Layout>
  );
}
